import styled from "styled-components";
import { Flex } from "rebass/styled-components";
import { ErrorBoundary } from "react-error-boundary";
import { useStatsigClient } from "@statsig/react-bindings";

import { Featured } from "./Featured";
import { ContentFeed } from "./ContentFeed";
import { SuggestedFeed } from "./SuggestedFeed";
import { CombinedFeed } from "./CombinedFeed";
import { COMBINED_FEED_CONFIGURATIONS } from "./CombinedFeed/configurations";
import { HomepageWrapper } from "./styledComponents";
import { HomepageViewModel } from "./ViewModel";
import { ESupportedGames } from "../../@types";
import { GAME_TO_ICON_MAP } from "../../Components/GameSelector";
import { CampaignWidget } from "../Competitions/Components/Widget";
import { useGamePreference } from "../../Hooks/useGamePreference";
import { UserBadge } from "MVVM/Components/UserBadge";
import { Top5Widget } from "MVVM/Components/Top5Widget";
import { Top5WidgetV2 } from "MVVM/Components/Top5WidgetV2";
import { useUser } from "MVVM/Hooks/useUser";

const Homepage = () => {
  const { contentFeeds, contentFeedsLoading, prosFeed, prosFeedLoading } =
    HomepageViewModel();
  const { gamePreference } = useGamePreference();

  const { getExperiment } = useStatsigClient();
  const { allstarUser } = useUser();

  const useCombinedFeed =
    allstarUser.loggedIn &&
    getExperiment("homepage_combined_for_you_feed").get("enabled", false);

  const configVariant = "custom4Plus3Plus3";

  const combinedFeedConfig =
    COMBINED_FEED_CONFIGURATIONS[
      configVariant as keyof typeof COMBINED_FEED_CONFIGURATIONS
    ] || COMBINED_FEED_CONFIGURATIONS.control;

  const showV2Widget = getExperiment("homepage_top5_widget").get("show_v2");

  const gameFeeds = [
    {
      game: ESupportedGames.CS2,
      title: "Counter-Strike 2",
      data: contentFeeds?.cs2?.data,
      icon: GAME_TO_ICON_MAP[ESupportedGames.CS2],
    },
    {
      game: ESupportedGames.LOL,
      title: "League of Legends",
      data: contentFeeds?.league?.data,
      icon: GAME_TO_ICON_MAP[ESupportedGames.LOL],
    },
    {
      game: ESupportedGames.FORTNITE,
      title: "Fortnite",
      data: contentFeeds?.fortnite?.data,
      icon: GAME_TO_ICON_MAP[ESupportedGames.FORTNITE],
    },
    {
      game: ESupportedGames.DOTA2,
      title: "Dota 2",
      data: contentFeeds?.dota2?.data,
      icon: GAME_TO_ICON_MAP[ESupportedGames.DOTA2],
    },
  ];

  const getOrderedGameFeeds = () => {
    const preferredFeed = gameFeeds.find(
      (feed) => feed.game === gamePreference,
    );
    const otherFeeds = gameFeeds.filter((feed) => feed.game !== gamePreference);

    return {
      preferredFeed,
      otherFeeds,
    };
  };

  const { preferredFeed, otherFeeds } = getOrderedGameFeeds();

  return (
    <HomepageWrapper>
      <HomepageContainer>
        <Featured />
        <ErrorBoundary fallback={<></>}>
          {showV2Widget ? <Top5WidgetV2 /> : <Top5Widget />}
        </ErrorBoundary>
        <ErrorBoundary fallback={<></>}>
          <CampaignWidget />
        </ErrorBoundary>
        {useCombinedFeed ? (
          <ErrorBoundary fallback={<></>}>
            <CombinedFeed {...combinedFeedConfig} />
          </ErrorBoundary>
        ) : (
          <>
            {prosFeed || prosFeedLoading ? (
              <ContentFeed
                title="From the Pros"
                data={prosFeed}
                loading={prosFeedLoading}
                isProsFeed={true}
                icon={
                  <UserBadge
                    userGroup="PRO_PLAYER"
                    style={{ marginRight: "8px" }}
                  />
                }
              />
            ) : (
              <ContentFeed
                title="For You"
                data={contentFeeds?.forYou?.nodes || contentFeeds?.forYou}
                loading={contentFeedsLoading}
              />
            )}
          </>
        )}
        {preferredFeed && (
          <ContentFeed
            key={preferredFeed.game}
            title={preferredFeed.title}
            data={preferredFeed.data}
            loading={contentFeedsLoading}
            icon={preferredFeed.icon}
          />
        )}
        <ErrorBoundary fallback={<></>}>
          <SuggestedFeed />
        </ErrorBoundary>
        <ContentFeed
          title="Recent Montages"
          data={contentFeeds?.montages?.data}
          loading={contentFeedsLoading}
          isMontageFeed={true}
        />
        {otherFeeds.map((feed) => (
          <ContentFeed
            key={feed.game}
            title={feed.title}
            data={feed.data}
            loading={contentFeedsLoading}
            icon={feed.icon}
          />
        ))}
      </HomepageContainer>
    </HomepageWrapper>
  );
};

const HomepageContainer = styled(Flex)`
  flex-direction: column;
  gap: 32px;
  max-width: 2480px;
  padding-bottom: 80px;
  width: 100vw;
`;

export default Homepage;
